// ================================================================
// Advertisement enums — mirrors RAK.Shared/Advertisement/AdvertisementEnums.cs
// The server has NO JsonStringEnumConverter configured, so every enum is sent
// and received as a raw integer over the wire. These numeric maps are the
// single source of truth on the JS side; keep them in sync with the C# enums.
// ================================================================

export const AdvertisementType = { Carousel: 0, Popup: 1, NewsBar: 2 };
export const AdvertisementMediaType = { Image: 0, Video: 1 };
export const AdvertisementPlatform = { Web: 0, Mobile: 1 };
export const AdvertisementPlatformFilter = { All: 0, Web: 1, Mobile: 2, Both: 3 };
export const AdvertisementPage = {
  Global: 0, Home: 1, Product: 2, Catalog: 3, Cart: 4, Checkout: 5, Account: 6,
};
export const AdvertisementSlot = {
  Top: 0,
  BelowNewArrivals: 1,
  BelowGiftSets: 2,
  BelowMenSection: 3,
  BelowWomenSection: 4,
  BelowDesignerSection: 5,
  BelowNicheSection: 6,
  BelowBestSellers: 7,
  BelowBrandsSection: 8,
  BottomOfPage: 9,
  BelowGallery: 20,
  BelowProductInfo: 21,
  BelowDescription: 22,
  AboveRelatedContent: 23,
  AboveProducts: 40,
  BelowProducts: 41,
  AboveHeader: 50,
  BelowHeader: 51,
  AboveFooter: 52,
};
export const AdvertisementActionType = {
  None: 0, Product: 1, Category: 2, Brand: 3, InternalPage: 4, ExternalUrl: 5,
};
export const AdvertisementStatus = { Draft: 0, Scheduled: 1, Active: 2, Disabled: 3, Expired: 4 };
export const AdvertisementInteractionType = { Impression: 0, Click: 1 };
export const PopupTriggerType = { Immediate: 0, AfterSeconds: 1, AfterScrollPercent: 2 };
export const PopupFrequency = { EveryVisit: 0, OncePerSession: 1, OncePerUser: 2, OncePerDay: 3 };

// ----------------------------------------------------------------
// Display-only label maps (mirrors of the C# name dictionaries used purely
// for rendering text — actual selectable Page/Slot OPTIONS always come live
// from GET /api/admin/advertisements/registry, never from these maps).
// ----------------------------------------------------------------

export const TYPE_LABELS = {
  [AdvertisementType.Carousel]: { en: 'Carousel / Banner', ar: 'كاروسيل / بانر' },
  [AdvertisementType.Popup]: { en: 'Popup', ar: 'نافذة منبثقة' },
  [AdvertisementType.NewsBar]: { en: 'News Bar', ar: 'شريط إخباري' },
};

export const STATUS_LABELS = {
  [AdvertisementStatus.Draft]: { en: 'Draft', ar: 'مسودة' },
  [AdvertisementStatus.Scheduled]: { en: 'Scheduled', ar: 'مجدول' },
  [AdvertisementStatus.Active]: { en: 'Active', ar: 'نشط' },
  [AdvertisementStatus.Disabled]: { en: 'Disabled', ar: 'معطل' },
  [AdvertisementStatus.Expired]: { en: 'Expired', ar: 'منتهي' },
};

export const STATUS_BADGE_CLASS = {
  [AdvertisementStatus.Draft]: 'badge-neutral',
  [AdvertisementStatus.Scheduled]: 'badge-info',
  [AdvertisementStatus.Active]: 'badge-success',
  [AdvertisementStatus.Disabled]: 'badge-warning',
  [AdvertisementStatus.Expired]: 'badge-danger',
};

export const PLATFORM_LABELS = {
  [AdvertisementPlatform.Web]: { en: 'Web', ar: 'الويب' },
  [AdvertisementPlatform.Mobile]: { en: 'Mobile', ar: 'الجوال' },
};

export const PLATFORM_FILTER_LABELS = {
  [AdvertisementPlatformFilter.All]: { en: 'All Platforms', ar: 'جميع المنصات' },
  [AdvertisementPlatformFilter.Web]: { en: 'Web Store Only', ar: 'المتجر الإلكتروني فقط' },
  [AdvertisementPlatformFilter.Mobile]: { en: 'Mobile App Only', ar: 'تطبيق الجوال فقط' },
  [AdvertisementPlatformFilter.Both]: { en: 'Web + Mobile', ar: 'الويب والجوال' },
};

export const ACTION_TYPE_LABELS = {
  [AdvertisementActionType.None]: { en: 'No Action', ar: 'بدون إجراء' },
  [AdvertisementActionType.Product]: { en: 'Product', ar: 'منتج' },
  [AdvertisementActionType.Category]: { en: 'Category', ar: 'تصنيف' },
  [AdvertisementActionType.Brand]: { en: 'Brand', ar: 'علامة تجارية' },
  [AdvertisementActionType.InternalPage]: { en: 'Internal Page', ar: 'صفحة داخلية' },
  [AdvertisementActionType.ExternalUrl]: { en: 'External URL', ar: 'رابط خارجي' },
};

export const TRIGGER_TYPE_LABELS = {
  [PopupTriggerType.Immediate]: { en: 'Immediately', ar: 'فوراً' },
  [PopupTriggerType.AfterSeconds]: { en: 'After N Seconds', ar: 'بعد عدد من الثواني' },
  [PopupTriggerType.AfterScrollPercent]: { en: 'After Scroll %', ar: 'بعد نسبة تمرير' },
};

export const FREQUENCY_LABELS = {
  [PopupFrequency.EveryVisit]: { en: 'Every Visit', ar: 'كل زيارة' },
  [PopupFrequency.OncePerSession]: { en: 'Once Per Session', ar: 'مرة كل جلسة' },
  [PopupFrequency.OncePerUser]: { en: 'Once Per User', ar: 'مرة لكل مستخدم' },
  [PopupFrequency.OncePerDay]: { en: 'Once Per Day', ar: 'مرة يومياً' },
};

// Mirrors AdvertisementPlacementRegistry.cs PageNames / SlotNames — display text only.
export const PAGE_NAMES = {
  [AdvertisementPage.Global]: { en: 'All Pages (Site-wide)', ar: 'كل الصفحات' },
  [AdvertisementPage.Home]: { en: 'Home', ar: 'الرئيسية' },
  [AdvertisementPage.Product]: { en: 'Product Details', ar: 'تفاصيل المنتج' },
  [AdvertisementPage.Catalog]: { en: 'Category / Brand / Search', ar: 'الفئات والماركات والبحث' },
  [AdvertisementPage.Cart]: { en: 'Cart', ar: 'السلة' },
  [AdvertisementPage.Checkout]: { en: 'Checkout', ar: 'الدفع' },
  [AdvertisementPage.Account]: { en: 'Account / Orders', ar: 'الحساب والطلبات' },
};

export const SLOT_NAMES = {
  [AdvertisementSlot.Top]: { en: 'Top', ar: 'الأعلى' },
  [AdvertisementSlot.BelowNewArrivals]: { en: 'Below New Arrivals', ar: 'أسفل وصل حديثاً' },
  [AdvertisementSlot.BelowGiftSets]: { en: 'Below Gift Sets', ar: 'أسفل الأطقم والهدايا' },
  [AdvertisementSlot.BelowMenSection]: { en: "Below Men's Section", ar: 'أسفل قسم الرجال' },
  [AdvertisementSlot.BelowWomenSection]: { en: "Below Women's Section", ar: 'أسفل قسم النساء' },
  [AdvertisementSlot.BelowDesignerSection]: { en: 'Below Designer Perfumes', ar: 'أسفل العطور التجارية' },
  [AdvertisementSlot.BelowNicheSection]: { en: 'Below Niche Perfumes', ar: 'أسفل العطور المتخصصة' },
  [AdvertisementSlot.BelowBestSellers]: { en: 'Below Best Sellers', ar: 'أسفل الأكثر مبيعاً' },
  [AdvertisementSlot.BelowBrandsSection]: { en: 'Below Brands', ar: 'أسفل الماركات' },
  [AdvertisementSlot.BottomOfPage]: { en: 'Bottom of Page', ar: 'أسفل الصفحة' },
  [AdvertisementSlot.BelowGallery]: { en: 'Below Image Gallery', ar: 'أسفل معرض الصور' },
  [AdvertisementSlot.BelowProductInfo]: { en: 'Below Product Info', ar: 'أسفل معلومات المنتج' },
  [AdvertisementSlot.BelowDescription]: { en: 'Below Description', ar: 'أسفل الوصف' },
  [AdvertisementSlot.AboveRelatedContent]: { en: 'Above Related Products / Comments', ar: 'أعلى المنتجات المشابهة' },
  [AdvertisementSlot.AboveProducts]: { en: 'Above Products', ar: 'أعلى المنتجات' },
  [AdvertisementSlot.BelowProducts]: { en: 'Below Products', ar: 'أسفل المنتجات' },
  [AdvertisementSlot.AboveHeader]: { en: 'Above Header', ar: 'أعلى الهيدر' },
  [AdvertisementSlot.BelowHeader]: { en: 'Below Header', ar: 'أسفل الهيدر' },
  [AdvertisementSlot.AboveFooter]: { en: 'Above Footer', ar: 'أعلى الفوتر' },
};

export const labelFor = (map, value, isRtl) => {
  const entry = map[value];
  if (!entry) return String(value ?? '-');
  return isRtl ? entry.ar : entry.en;
};

// ----------------------------------------------------------------
// Iraq (UTC+3, no DST) datetime helpers.
// The admin's browser may be in any timezone, so all datetime-local inputs
// are interpreted as Baghdad local time explicitly via a fixed +3h offset,
// never via the browser's own timezone.
// ----------------------------------------------------------------

const IRAQ_OFFSET_MINUTES = 3 * 60;

/** UTC ISO string -> "YYYY-MM-DDTHH:mm" for a datetime-local input, in Iraq time. */
export const toIraqLocalInputValue = (utcIso) => {
  if (!utcIso) return '';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '';
  const shifted = new Date(d.getTime() + IRAQ_OFFSET_MINUTES * 60000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
};

/** "YYYY-MM-DDTHH:mm" (typed by the admin, meant as Iraq local time) -> UTC ISO string. */
export const fromIraqLocalInputValue = (localValue) => {
  if (!localValue) return null;
  const [datePart, timePart] = localValue.split('T');
  if (!datePart || !timePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;
  const utcMs = Date.UTC(y, m - 1, d, hh, mm) - IRAQ_OFFSET_MINUTES * 60000;
  return new Date(utcMs).toISOString();
};

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

/** UTC ISO string -> human-readable Iraq-local date+time, independent of the viewer's browser timezone. */
export const formatIraqDateTime = (utcIso, isRtl) => {
  if (!utcIso) return '-';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '-';
  const shifted = new Date(d.getTime() + IRAQ_OFFSET_MINUTES * 60000);
  const day = shifted.getUTCDate();
  const month = shifted.getUTCMonth();
  const year = shifted.getUTCFullYear();
  const hours24 = shifted.getUTCHours();
  const minutes = shifted.getUTCMinutes();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const pad = (n) => String(n).padStart(2, '0');
  if (isRtl) {
    const ampm = hours24 < 12 ? 'ص' : 'م';
    return `${day} ${MONTHS_AR[month]} ${year}، ${pad(hours12)}:${pad(minutes)} ${ampm}`;
  }
  const ampm = hours24 < 12 ? 'AM' : 'PM';
  return `${MONTHS_EN[month]} ${day}, ${year}, ${pad(hours12)}:${pad(minutes)} ${ampm}`;
};

export const formatIraqDateOnly = (utcIso, isRtl) => {
  if (!utcIso) return '-';
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return '-';
  const shifted = new Date(d.getTime() + IRAQ_OFFSET_MINUTES * 60000);
  const day = shifted.getUTCDate();
  const month = shifted.getUTCMonth();
  const year = shifted.getUTCFullYear();
  return isRtl ? `${day} ${MONTHS_AR[month]} ${year}` : `${MONTHS_EN[month]} ${day}, ${year}`;
};